// =============================================================================
// GAME OVER SCREEN - Екран програшу (Tactical HUD)
// =============================================================================

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { GAME_OVER_TEXTS, type GameOverType } from '../types';
import { trackGameOver, trackRetry, trackFeedbackModalOpen, trackFeedbackModalDismiss } from '../services/analytics';
import { hasSubmittedFeedback } from '../services/feedbackService';
import { ShareCardModal } from './sharing/ShareCardModal';
import { ShareStoriesModal } from './sharing/ShareStoriesModal';
import { FeedbackModal } from './FeedbackModal';
import type {
  GameStateData,
  DecisionData,
} from '../services/storiesCardGenerator';

interface GameOverScreenProps {
  type: GameOverType;
  decisionNumber: number;
  finalStats: {
    militaryStrength: number;
    economy: number;
    resilience: number;
    westernSupport: number;
    trust: number;
  };
  onRestart: () => void;
}

export function GameOverScreen({
  type,
  decisionNumber,
  finalStats,
  onRestart,
}: GameOverScreenProps) {
  const texts = GAME_OVER_TEXTS[type];

  // Track game over on mount
  useEffect(() => {
    trackGameOver({
      type,
      decisionNumber,
      finalStats,
    });
  }, []);

  const handleRestart = () => {
    trackRetry('gameOver', decisionNumber);
    onRestart();
  };

  // Share functionality
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [storiesModalOpen, setStoriesModalOpen] = useState(false);

  // Feedback functionality
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [showFeedbackButton, setShowFeedbackButton] = useState(true);

  // Check if user already submitted feedback
  useEffect(() => {
    if (hasSubmittedFeedback()) {
      setShowFeedbackButton(false);
    }
  }, []);

  const handleFeedbackOpen = () => {
    trackFeedbackModalOpen('gameOver');
    setFeedbackModalOpen(true);
  };

  const handleFeedbackClose = () => {
    if (!feedbackModalOpen) return;
    trackFeedbackModalDismiss('gameOver');
    setFeedbackModalOpen(false);
    setShowFeedbackButton(false);
  };

  // Stories data for game over
  const storiesGameState: GameStateData = {
    turn: decisionNumber,
    maxTurns: 20,
    date: new Date().toLocaleDateString('uk-UA'),
    location: 'Бункер Генштабу, Київ',
    ...finalStats,
  };

  const storiesDecision: DecisionData = {
    situation: texts.description,
    chosenOption: texts.title,
    consequences: [{ text: texts.description, type: 'failure' }],
    criticality: 'critical',
  };

  // Get failure reason based on lowest stat
  const getFailureReason = () => {
    const stats = Object.entries(finalStats);
    const lowestStat = stats.reduce((min, current) =>
      current[1] < min[1] ? current : min
    );

    const reasonMap: Record<string, string> = {
      militaryStrength: 'КРИТИЧНА ВТРАТА БОЄЗДАТНОСТІ',
      economy: 'ЕКОНОМІЧНИЙ КОЛАПС',
      resilience: 'ВТРАТА СТІЙКОСТІ НАЦІЇ',
      westernSupport: 'ВТРАТА ПІДТРИМКИ ЗАХОДУ',
      trust: 'ВТРАТА ДОВІРИ НАРОДУ',
    };

    return reasonMap[lowestStat[0]] || 'СИСТЕМНИЙ ПРОВАЛ';
  };

  const failureReason = getFailureReason();

  // Get stat label in Ukrainian
  const getStatLabel = (key: string) => {
    const labels: Record<string, string> = {
      militaryStrength: 'ВІЙСЬК_СИЛА',
      economy: 'ЕКОНОМІКА',
      resilience: 'СТІЙКІСТЬ',
      westernSupport: 'ПІДТР_ЗАХОДУ',
      trust: 'ДОВІРА',
    };
    return labels[key] || key;
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden" style={{ backgroundColor: '#050505' }}>
      {/* Subtle scanline effect */}
      <div className="absolute inset-0 pointer-events-none opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,0,0,0.03) 2px, rgba(255,0,0,0.03) 4px)'
        }} />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-2xl w-full relative"
      >
        {/* Terminal Header Bar */}
        <div className="flex items-center justify-between px-4 py-2 border border-[#ff3333]/30 bg-[#0a0a0a] mb-0">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-[#ff3333] animate-pulse" />
            <span className="font-mono text-[10px] text-[#ff3333] uppercase tracking-widest">
              SYSTEM_FAILURE // CRITICAL_ALERT
            </span>
          </div>
          <span className="font-mono text-[10px] text-gray-500 uppercase">
            CODE: {type}
          </span>
        </div>

        {/* Main Content */}
        <div className="border-x border-b border-[#ff3333]/30 bg-[#0a0a0a] p-6 md:p-8">

          {/* Main Title - Left Aligned */}
          <div className="mb-8">
            <motion.h1
              animate={{ opacity: [1, 0.7, 1] }}
              transition={{ repeat: Infinity, duration: 1.5, ease: 'easeInOut' }}
              className="font-mono text-4xl md:text-5xl lg:text-6xl font-black text-[#ff3333] uppercase tracking-tight leading-none text-left"
            >
              ГРА ЗАКІНЧЕНА
            </motion.h1>
            <p className="font-mono text-sm md:text-base text-gray-400 mt-3 leading-relaxed text-left">
              Війна — ні. А боротьба? Тільки починається.
            </p>
          </div>

          {/* Failure Reason - Error Log Style */}
          <div className="mb-8 p-4 border-l-4 border-[#ff3333] bg-[#ff3333]/5">
            <div className="flex items-start gap-3">
              <span className="text-[#ff3333] text-2xl">⚠</span>
              <div className="text-left">
                <span className="font-mono text-[10px] text-gray-500 uppercase tracking-wider block mb-1">
                  ПРИЧИНА ПОРАЗКИ:
                </span>
                <span className="font-mono text-lg md:text-xl text-[#ff3333] font-bold uppercase">
                  {failureReason}
                </span>
              </div>
            </div>
          </div>

          {/* System Description */}
          <div className="mb-8 text-left">
            <span className="font-mono text-[10px] text-gray-500 uppercase tracking-wider block mb-2">
              &gt; SYSTEM_LOG:
            </span>
            <p className="font-mono text-sm text-gray-300 leading-[1.6]">
              {texts.description}
            </p>
          </div>

          {/* Stats Grid */}
          <div className="mb-8">
            <span className="font-mono text-[10px] text-gray-500 uppercase tracking-wider block mb-3 text-left">
              &gt; FINAL_METRICS [DECISION #{String(decisionNumber).padStart(2, '0')}]:
            </span>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {Object.entries(finalStats).map(([key, val]) => {
                const isCritical = val < 20;
                const isZero = val === 0;
                return (
                  <div
                    key={key}
                    className={`p-3 font-mono text-left border ${isZero
                      ? 'border-[#ff3333] bg-[#ff3333]/10'
                      : isCritical
                        ? 'border-[#ff3333]/50 bg-[#ff3333]/5'
                        : 'border-gray-800 bg-[#0d0d0d]'
                      } ${isZero ? 'animate-pulse' : ''}`}
                  >
                    <span className={`text-[9px] uppercase tracking-wider block mb-1 ${isCritical ? 'text-[#ff3333]/70' : 'text-gray-600'
                      }`}>
                      {getStatLabel(key)}
                    </span>
                    <span className={`text-lg font-bold ${isZero ? 'text-[#ff3333]' : isCritical ? 'text-[#ff3333]' : 'text-gray-400'
                      }`}>
                      {Math.round(val)}%
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3 pt-4 border-t border-gray-800">
            {/* Primary Action - Return to HQ */}
            <button
              onClick={handleRestart}
              className="w-full py-4 border-2 border-[#10b981] text-[#10b981] hover:bg-[#10b981] hover:text-black transition-all font-mono font-bold uppercase tracking-widest text-sm group"
            >
              <span className="group-hover:tracking-[0.3em] transition-all">
                ПОВЕРНУТИСЯ ДО ШТАБУ
              </span>
            </button>

            {/* Secondary Actions Row */}
            <div className="flex gap-3">
              {/* Stories Export - Wireframe Style */}
              <button
                onClick={() => setStoriesModalOpen(true)}
                className="flex-1 py-3 border border-gray-700 text-gray-500 hover:text-gray-300 hover:border-gray-500 transition-all font-mono text-xs uppercase tracking-wider"
              >
                [ EXPORT_LOG_TO_IG ]
              </button>

              {/* Share - Simple Wireframe */}
              <button
                onClick={() => setShareModalOpen(true)}
                className="flex-1 py-3 border border-gray-700 text-gray-500 hover:text-gray-300 hover:border-gray-500 transition-all font-mono text-xs uppercase tracking-wider"
              >
                [ SHARE_REPORT ]
              </button>
            </div>

            {/* Feedback Link */}
            {showFeedbackButton && (
              <button
                onClick={handleFeedbackOpen}
                className="w-full py-2 text-gray-600 hover:text-gray-400 transition-colors font-mono text-[10px] uppercase tracking-wider flex items-center justify-center gap-2"
              >
                <span>—</span>
                ZV'YAZOK_ZI_SHTABOM
                <span>—</span>
              </button>
            )}
          </div>

          {/* Beta & Social Links */}
          <div className="mt-8 pt-4 border-t border-gray-800/50">
            <div className="text-left space-y-3">
              <div className="inline-block px-2 py-0.5 border border-[#ff3333]/30 text-[#ff3333]/70 font-mono text-[9px] uppercase tracking-widest">
                BETA v0.9
              </div>
              <p className="font-mono text-[11px] text-gray-600 leading-[1.5]">
                Гра у розробці. Якщо сподобалось — дайте знати через соцмережі.
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-[9px] text-gray-700 uppercase">
                  CHANNELS:
                </span>
                {[
                  { label: 'TG', href: 'https://t.me/zavoruha_tech' },
                  { label: 'IG', href: 'https://www.instagram.com/zavoruha' },
                  { label: 'TR', href: 'https://www.threads.com/@zavoruha' },
                  { label: 'FB', href: 'https://www.facebook.com/profile.php?id=61587632097637' },
                ].map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-[10px] text-gray-600 hover:text-[#10b981] transition-colors"
                  >
                    [{link.label}]
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer Status */}
        <div className="flex justify-between items-center px-4 py-2 border border-t-0 border-gray-800 bg-[#080808]">
          <span className="font-mono text-[8px] text-gray-700 uppercase">
            SESSION_TERMINATED
          </span>
          <span className="font-mono text-[8px] text-gray-700 uppercase">
            STRATCOM_UA // {new Date().toLocaleDateString('uk-UA')}
          </span>
        </div>
      </motion.div>

      {/* Share Card Modal */}
      <ShareCardModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        stats={finalStats}
        decisionNumber={decisionNumber}
        isGameOver={true}
        gameOverType={type}
      />

      {/* Instagram Stories Modal */}
      <ShareStoriesModal
        isOpen={storiesModalOpen}
        onClose={() => setStoriesModalOpen(false)}
        gameState={storiesGameState}
        decision={storiesDecision}
      />

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={feedbackModalOpen}
        onClose={handleFeedbackClose}
        gameResult="gameOver"
        decisionNumber={decisionNumber}
      />
    </div>
  );
}
